import React, { useState } from "react";
import { Download, FileJson, FolderArchive, ShieldAlert, PauseCircle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SectionShell, SCard } from "./SettingsUI";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/i18n";

const download = async (format) => {
  const r = await api.get(`/account/export?format=${format}`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `athlynx_export.${format === "csv" ? "zip" : "json"}`;
  a.click();
  URL.revokeObjectURL(url);
};

const ConfirmDialog = ({ open, onOpenChange, mode, hasPassword, onDone }) => {
  const { tf } = useLang();
  const { logout } = useAuth();
  const isDelete = mode === "delete";
  const word = isDelete ? "SUPPRIMER" : "DESACTIVER";
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/account/${isDelete ? "delete" : "deactivate"}`, { confirm, password: password || null });
      toast.success(isDelete ? tf("Compte supprimé.") : tf("Compte désactivé."));
      onOpenChange(false);
      onDone?.();
      setTimeout(() => logout(), 800);
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid={`${mode}-dialog`}>
        <DialogHeader>
          <DialogTitle>{isDelete ? tf("Supprimer définitivement le compte") : tf("Désactiver le compte")}</DialogTitle>
          <DialogDescription className="leading-relaxed">
            {isDelete
              ? tf("Cette action entraînera la suppression définitive de votre compte Athlynx et des données associées, conformément aux règles de conservation applicables. Vos sessions seront immédiatement révoquées.")
              : tf("Votre accès sera suspendu et tous vos appareils déconnectés. Vos données sont conservées : reconnectez-vous à tout moment pour réactiver votre compte.")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          {hasPassword && (
            <div className="grid gap-2">
              <Label>{tf("Mot de passe")}</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid={`${mode}-password-input`} autoComplete="current-password" />
            </div>
          )}
          <div className="grid gap-2">
            <Label>{tf("Saisissez «")} {word} {tf("» pour confirmer")}</Label>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={word} data-testid={`${mode}-confirm-input`} />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{tf("Annuler")}</Button>
          <Button variant={isDelete ? "destructive" : "default"} disabled={busy || confirm.trim().toUpperCase() !== word}
            onClick={run} data-testid={`${mode}-confirm-btn`}>
            {busy ? tf("En cours…") : isDelete ? tf("Supprimer mon compte") : tf("Désactiver mon compte")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const SectionData = ({ overview, reload }) => {
  const { tf } = useLang();
  const hasPassword = Boolean(overview?.security?.has_password);
  const [dialog, setDialog] = useState(null);
  const [exporting, setExporting] = useState(null);

  const doExport = async (format) => {
    setExporting(format);
    try {
      await download(format);
      toast.success(tf("Export téléchargé"));
    } catch { toast.error(tf("Export impossible")); }
    finally { setExporting(null); }
  };

  return (
    <SectionShell title={tf("Données & confidentialité")} desc={tf("Contrôlez vos données et gérez votre compte, dans une logique RGPD.")} testid="section-data">
      <SCard title={tf("Mes données")} desc={tf("Téléchargez une copie de vos données Athlynx : profil, athlètes, contrats, interactions, journal d'activité. Aucun mot de passe ni secret n'est inclus.")} testid="data-export-card">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => doExport("json")} disabled={exporting === "json"} data-testid="export-json-btn">
            <FileJson className="h-4 w-4 mr-1.5" />{exporting === "json" ? tf("Export…") : tf("Exporter en JSON")}
          </Button>
          <Button variant="outline" onClick={() => doExport("csv")} disabled={exporting === "csv"} data-testid="export-csv-btn">
            <FolderArchive className="h-4 w-4 mr-1.5" />{exporting === "csv" ? tf("Export…") : tf("Exporter en CSV (ZIP)")}
          </Button>
        </div>
      </SCard>

      <SCard title={tf("Confidentialité")} testid="data-privacy-card">
        <p className="text-sm text-muted-foreground leading-relaxed">
          {tf("Vos données sont utilisées uniquement pour le fonctionnement de votre espace Athlynx. Elles ne sont ni revendues ni utilisées pour entraîner des modèles d'IA publics.")}
        </p>
      </SCard>

      <SCard title={tf("Zone sensible")} testid="danger-zone-card" right={<ShieldAlert className="h-4 w-4 text-red-400/70" />}
        className="border-red-500/[0.15]">
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-white/[0.06] p-4">
            <div>
              <p className="text-sm font-medium">{tf("Désactiver le compte")}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{tf("Accès suspendu, données conservées. Réactivation en vous reconnectant.")}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setDialog("deactivate")} data-testid="deactivate-btn">
              <PauseCircle className="h-3.5 w-3.5 mr-1.5" /> {tf("Désactiver")}
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-red-500/[0.18] bg-red-500/[0.03] p-4">
            <div>
              <p className="text-sm font-medium">{tf("Supprimer le compte")}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{tf("Suppression définitive avec purge différée conforme aux obligations légales.")}</p>
            </div>
            <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/[0.08] hover:text-red-400" onClick={() => setDialog("delete")} data-testid="delete-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {tf("Supprimer")}
            </Button>
          </div>
        </div>
      </SCard>

      <ConfirmDialog open={dialog === "deactivate"} onOpenChange={(o) => !o && setDialog(null)} mode="deactivate" hasPassword={hasPassword} onDone={reload} />
      <ConfirmDialog open={dialog === "delete"} onOpenChange={(o) => !o && setDialog(null)} mode="delete" hasPassword={hasPassword} onDone={reload} />
    </SectionShell>
  );
};
